/**
 * The scene: turns world state into pixels, sixty times a second.
 *
 * Nothing here writes to the simulation. It reads `World`, interpolates between
 * the last two 30 Hz sim states, and drains the per-tick event list for effects.
 *
 * The readability rules from the brief are implemented here and are not
 * negotiable:
 *   • chunky silhouettes with a warm rim for yours, cold for theirs
 *   • health bars only on the damaged, and always on champions and siege
 *   • white flash + knockback nudge + squash on every hit
 *   • hitstop scaled to damage, screen shake, zoom punch
 *   • past a zoom threshold, soldiers become war-map tokens
 */

import { CanvasSource, Container, Graphics, Sprite, Texture, type Application } from 'pixi.js';
import type { TextureBank} from './textures.ts';
import { ART_TILE_PX, ART_UNIT_PX, tokenIndexFor } from './textures.ts';
import { Camera, ZOOM_TOKEN_THRESHOLD } from './camera.ts';
import { Fx } from './fx.ts';
import { buildingArt, propTexture, sheetDir, unitSheet } from './spritedrop.ts';
import { C, mix, shade } from '../data/palette.ts';
import { unitType } from '../data/units.ts';
import { buildingType } from '../data/buildings.ts';
import { faction } from '../data/factions.ts';
import { MAP_H, MAP_W, MAX_UNITS } from '../sim/constants.ts';
import { Phase, Team, UF, UnitState, WallKind, Terrain, type FieldObstacle } from '../sim/types.ts';
import { EV, type SimEvent } from '../sim/events.ts';
import { ZoneKind, type World } from '../sim/world.ts';
import { CurtainWatch, TOWER_SIZE } from '../sim/systems/curtain.ts';
import { DAY_SECONDS } from '../sim/systems/calendar.ts';
import { Errand, errandOf, hasArrived } from '../sim/systems/villagers.ts';
import { ChatterBubbles, bubbleFor, moodOf } from './chatter.ts';
import { PROP_GROUND, PROP_SPREAD, obstacleArt } from './scatter.ts';
import { NODE_GROUND, nodeArt, nodeSize } from './nodeart.ts';
import { rubbleArt, wreckArt } from './wreckart.ts';
import { drawGatehouse, drawTower, drawWallTile, TOWER_RISE, WALL_RISE } from './curtainart.ts';
import { vernacularOf, type Vernacular } from './vernacular.ts';
import { themeFor, type BiomeTheme } from './biome.ts';
import { GroundBaker } from './ground.ts';
import { villagerSheet } from './folk.ts';
import { nodeReadout, nodesInBox } from '../sim/systems/nodes.ts';
import { fieldObstacles } from '../sim/obstacles.ts';
import { clamp, clamp01, lerp, wrapAngle } from '../core/math.ts';

const CHUNK = 16;
/**
 * How many chunks beyond the view keep their baked texture.
 *
 * One screen-width of slack in every direction: enough that ordinary panning
 * never re-bakes, small enough that resident terrain stays a bounded constant.
 */
const CHUNK_KEEP = 2;
const CHUNKS_X = Math.ceil(MAP_W / CHUNK);
const CHUNKS_Y = Math.ceil(MAP_H / CHUNK);

/** Contact frame is held for this fraction of the swing, so the blow lands. */
const CONTACT_HOLD = 0.13;

export interface SelectionState {
  companies: number[];
  hoverCompany: number;
  /** Building slot index the player has open, or −1. */
  building?: number;
}

export class Scene {
  readonly camera = new Camera();
  readonly fx: Fx;

  readonly root = new Container();
  readonly terrainLayer = new Container();
  readonly decalLayer: Container;
  readonly zoneLayer = new Container();
  readonly structureLayer = new Container();
  /**
   * The curtain's third dimension: tower bodies, drawn above the flat wall tiles
   * and below the men standing on them.
   *
   * Its own layer rather than one more sprite pool in `structureLayer`, because
   * the ordering has to be exact — a tower has to cover the tiles it stands on
   * and be covered by its own garrison, or the whole "they are up there" read
   * collapses into "they are somewhere near a wall".
   */
  readonly curtainLayer = new Container();
  readonly shadowLayer = new Container();
  /**
   * Team discs. A soft coloured pool of light on the ground under every soldier
   * — warm for yours, cold for theirs. It is the cheapest and by far the most
   * effective friend/foe cue there is: it survives a 300-unit pile-up, it works
   * at every zoom level, and it reads before you have processed a single
   * silhouette. Everything else is detail on top of this.
   */
  readonly discLayer = new Container();
  readonly unitLayer = new Container();
  readonly overlayLayer = new Container();
  readonly particleLayer: Container;
  readonly additiveLayer: Container;
  readonly uiWorldLayer = new Container();
  /**
   * What the town is saying. Above everything, including the night tint — a
   * bubble the player has to squint through after dusk is a bubble that only
   * works half the day.
   */
  private readonly chatter = new ChatterBubbles();

  /** Piles where a building came down. See `drawRubble`. */
  private readonly rubbleSprites: Sprite[] = [];

  /** Standing wood, stone and ore. Culled per frame; see `drawNodes`. */
  private readonly nodeSprites: Sprite[] = [];
  private readonly nodeVisible: number[] = [];

  /** Battlefield scatter. See `drawScatter` for why the list is held, not re-read. */
  private readonly scatterSprites: Sprite[] = [];
  private readonly scatterShadows: Sprite[] = [];
  private scatterList: FieldObstacle[] = [];
  private scatterOf: readonly FieldObstacle[] | null = null;

  private zoneG = new Graphics();
  /** The day/night tint. See the layer-order note in the constructor. */
  readonly nightLayer = new Container();
  private nightG = new Graphics();
  private overlayG = new Graphics();
  private selectionG = new Graphics();
  private previewG = new Graphics();
  private curtainG = new Graphics();
  /** Breach and open-end marks. Above the men, because that is the point of them. */
  private markG = new Graphics();
  private readonly curtain = new CurtainWatch();
  /**
   * The people whose masonry the curtain is drawn in, cached on their id.
   *
   * A siege draws several hundred wall tiles a frame and `vernacularOf` walks a
   * table; the answer changes when the world does and at no other time. Held
   * beside `bank.vernacularId` rather than read off it, because that one is the
   * *building* bank's key and the two are the same today only by coincidence.
   */
  private vern: Vernacular | null = null;
  private vernId = '';

  /**
   * Draw the circuit's faults — the dangling ends that mean the ring does not
   * close. Set by `main.ts` from the phase and the armed tool: it is building
   * information, and a siege has enough on screen already. Breaches are drawn
   * regardless, because a breach is a thing that is happening.
   */
  showCurtain = false;

  private chunkSprites: Array<Sprite | null> = new Array(CHUNKS_X * CHUNKS_Y).fill(null);
  private chunkVersion: number[] = new Array(CHUNKS_X * CHUNKS_Y).fill(-1);

  /**
   * The ground under the marks: colour, shape and the country's own grain.
   *
   * See `render/ground.ts`. Held here rather than in the bank because it is
   * per-chunk work with per-chunk buffers, and the bank's job is textures that
   * are baked once — this one is baked as the camera reaches ground and again
   * whenever `terrainVersion` moves.
   */
  private readonly groundBaker = new GroundBaker(CHUNK);
  private groundImage: ImageData | null = null;
  /**
   * The country and the seed the resident chunks were baked for.
   *
   * When either moves — a different capital, a different run, a battle on
   * somebody else's ground — every baked chunk is a picture of the wrong place,
   * and `terrainVersion` will not say so: a fresh world starts at zero, which is
   * exactly what the stale sprites are already holding.
   */
  private groundKey = '';
  private theme: BiomeTheme = themeFor(undefined);

  private unitShadows: Sprite[] = [];
  /** Contact shadows for the wood, stone and ore. See `drawNodes`. */
  private nodeShadows: Sprite[] = [];
  private unitDiscs: Sprite[] = [];
  private unitBodies: Sprite[] = [];
  private unitWeapons: Sprite[] = [];
  private structureSprites: Sprite[] = [];
  private tileSprites: Sprite[] = [];
  private projSprites: Sprite[] = [];

  // Interpolation snapshots of the previous sim tick.
  private prevX = new Float32Array(MAX_UNITS);
  private prevY = new Float32Array(MAX_UNITS);
  private prevFacing = new Float32Array(MAX_UNITS);
  private prevValid = new Uint8Array(MAX_UNITS);

  /** Per-unit render-only squash, driven by hits. */
  private squash = new Float32Array(MAX_UNITS);

  /** Rolling combat log, drained by the HUD. */
  readonly log: Array<{ text: string; tone: number; t: number }> = [];
  /** Boss name-card request, drained by the HUD. */
  bossCard: { name: string; title: string; t: number } | null = null;

  private time = 0;
  /** Which people the building bank is currently baked for. See `render`. */
  private vernacularId = '';

  constructor(
    readonly app: Application,
    readonly bank: TextureBank,
  ) {
    this.fx = new Fx(bank);
    this.decalLayer = this.fx.decalLayer;
    this.particleLayer = this.fx.particleLayer;
    this.additiveLayer = this.fx.additiveLayer;

    this.root.addChild(this.terrainLayer);
    this.root.addChild(this.decalLayer);
    this.root.addChild(this.zoneLayer);
    this.zoneLayer.addChild(this.zoneG);
    this.root.addChild(this.shadowLayer);
    this.root.addChild(this.discLayer);
    this.root.addChild(this.structureLayer);
    this.root.addChild(this.curtainLayer);
    this.curtainLayer.addChild(this.curtainG);
    this.root.addChild(this.unitLayer);
    /**
     * The night, between the men and the fires.
     *
     * Above units and structures so the whole town genuinely darkens, and
     * *below* the particle and additive layers on purpose: torchlight, powers
     * and burning buildings punch through the dark rather than being dimmed by
     * it, which is the entire look — a siege at night is black shapes and
     * firelight, and it costs one screen-sized rect.
     */
    this.root.addChild(this.nightLayer);
    this.nightLayer.addChild(this.nightG);
    this.nightLayer.eventMode = 'none';
    this.root.addChild(this.particleLayer);
    this.root.addChild(this.additiveLayer);
    this.root.addChild(this.overlayLayer);
    this.overlayLayer.addChild(this.selectionG);
    this.overlayLayer.addChild(this.overlayG);
    this.overlayLayer.addChild(this.previewG);
    this.overlayLayer.addChild(this.markG);
    this.root.addChild(this.uiWorldLayer);
    this.uiWorldLayer.addChild(this.chatter.layer);

    for (const l of [
      this.terrainLayer,
      this.zoneLayer,
      this.shadowLayer,
      this.discLayer,
      this.structureLayer,
      this.curtainLayer,
      this.unitLayer,
      this.overlayLayer,
    ]) {
      l.eventMode = 'none';
    }
    this.shadowLayer.alpha = 0.42;
    this.discLayer.blendMode = 'add';
    this.discLayer.alpha = 0.55;

    app.stage.addChild(this.root);
  }

  resize(w: number, h: number): void {
    this.camera.resize(w, h);
  }

  /**
   * The building under a screen point, or −1.
   *
   * Screen → world → tile → `map.building`, which is the occupancy index the
   * simulation already keeps for exactly this question: every tile of a
   * footprint carries its building's slot, so there is no footprint arithmetic
   * to get subtly wrong and no sweep of the store per click.
   *
   * The art is drawn *above* the footprint — a tower's roof stands a couple of
   * tiles clear of the ground it occupies — so the tile under the pointer is
   * only half the answer. If the tile is empty, walk down the column a short
   * way: clicking a spire should select the tower it belongs to, which is what
   * the eye means by clicking it. Ground first, so a hut in front of a tower is
   * never stolen by the tower behind it.
   */
  buildingAt(w: World, screenX: number, screenY: number): number {
    const wx = Math.floor(this.camera.screenToWorldX(screenX));
    const wy = Math.floor(this.camera.screenToWorldY(screenY));
    if (!w.map.inBounds(wx, wy)) return -1;
    const here = w.map.building[w.map.idx(wx, wy)];
    if (here >= 0) return here;
    // How much headroom the tallest building has, in tiles. Beyond this a click
    // is on the ground behind, not on anything's roof.
    const REACH = 3;
    for (let d = 1; d <= REACH; d++) {
      const y = wy + d;
      if (!w.map.inBounds(wx, y)) break;
      const i = w.map.building[w.map.idx(wx, y)];
      if (i < 0) continue;
      // Only claim the click if the art actually reaches up this far.
      const def = buildingType(w.buildings.typeId[i]);
      if (def.vis.height * 1.4 >= d) return i;
    }
    return -1;
  }

  /** Call immediately before every sim step so the renderer can interpolate. */
  capturePrev(w: World): void {
    const u = w.units;
    this.prevValid.fill(0);
    for (let i = 0; i < u.slots.high; i++) {
      if (u.slots.alive[i] === 0) continue;
      this.prevX[i] = u.x[i];
      this.prevY[i] = u.y[i];
      this.prevFacing[i] = u.facing[i];
      this.prevValid[i] = 1;
    }
  }

  // ── Events → effects ─────────────────────────────────────────────────────

  consumeEvents(w: World): void {
    for (const e of w.events) this.handleEvent(w, e);
  }

  private handleEvent(w: World, e: SimEvent): void {
    const cam = this.camera;
    const onScreen = this.isVisible(e.x, e.y);

    switch (e.k) {
      case EV.Hit: {
        const cold = e.team !== Team.Player;
        if (onScreen) {
          this.fx.bloodBurst(e.x, e.y, e.dx, e.dy, e.a, cold);
          if (e.a > 14) this.fx.dust(e.x, e.y, 2);
          this.fx.floatNumber(e.x, e.y, Math.round(e.a).toString(), cold ? C.goldPale : C.oxbloodLight, clamp(e.a / 40, 0.7, 1.6));
        }
        // Hitstop: 60–110 ms, scaled to damage. The single best feel investment.
        const stop = clamp(0.06 + e.a * 0.0011, 0.06, 0.11);
        if (e.a > 12) this.fx.freeze(stop);
        if (e.a > 45) {
          cam.shake(clamp(e.a * 0.06, 2, 11));
          cam.zoomPunch(clamp(e.a * 0.0004, 0.01, 0.045));
        }
        break;
      }
      case EV.Block: {
        if (onScreen) {
          this.fx.sparks(e.x, e.y, -e.dx, -e.dy, 7);
          this.fx.floatNumber(e.x, e.y, 'block', C.boneDim, 0.7);
        }
        this.fx.freeze(0.045);
        break;
      }
      case EV.Death: {
        const big = e.a >= 2;
        if (onScreen) {
          this.fx.bloodBurst(e.x, e.y, e.dx, e.dy, big ? 60 : 22, e.team !== Team.Player);
          // A body, then the stain it leaves. Both small: the field should look
          // littered, not paved.
          this.fx.decal(
            this.bank.blood,
            e.x,
            e.y,
            big ? 2.0 : 1.15,
            e.team === Team.Player ? 0x4a1d1c : 0x2c3444,
            0.4,
            Math.atan2(e.dy, e.dx),
            300,
          );
          this.fx.decal(
            this.bank.corpse,
            e.x,
            e.y,
            big ? 1.7 : 1.0,
            e.team === Team.Player ? 0x4a4038 : 0x3d4756,
            0.62,
            Math.atan2(e.dy, e.dx),
            240,
          );
          if (big) {
            this.fx.dust(e.x, e.y, 8);
            this.fx.smokePlume(e.x, e.y, 3);
            cam.shake(7);
            // The 0.35x beat when something important dies.
            this.fx.slowMotion(0.35, 0.5);
            this.fx.freeze(0.12);
          }
        }
        break;
      }
      case EV.Shoot: {
        if (onScreen && e.b !== 1) this.fx.dust(e.x, e.y, 1, 0x3a3730);
        break;
      }
      case EV.Impact: {
        if (!onScreen) break;
        const kind = e.b;
        if (kind === 90) {
          // Starfall.
          this.fx.shockRing(e.x, e.y, e.a, C.goldPale, 0.6);
          this.fx.embers(e.x, e.y, 26, 4);
          this.fx.smokePlume(e.x, e.y, 8);
          this.fx.decal(this.bank.crater, e.x, e.y, e.a * 1.6, 0x1a1614, 0.7, 0, 400);
          cam.shake(22, 0.5);
          cam.zoomPunch(0.06);
          this.fx.freeze(0.14);
        } else if (kind === 91) {
          this.fx.embers(e.x, e.y, 6, 1.2);
          this.fx.decal(this.bank.scorch, e.x, e.y, 1.6, 0x120f0d, 0.35, 0, 90);
        } else if (kind === 92) {
          this.fx.sparks(e.x, e.y, e.dx, e.dy, 10);
          this.fx.shockRing(e.x, e.y, 0.8, 0x9fc7ff, 0.28);
        } else if (kind === 93) {
          // Cavalry charge connecting.
          this.fx.dust(e.x, e.y, 7, 0x554d42);
          this.fx.shockRing(e.x, e.y, 1.1, C.bone, 0.3);
          cam.shake(6);
          this.fx.freeze(0.09);
        } else if (kind === 20) {
          this.fx.dust(e.x, e.y, 2, 0x4a443b);
        } else {
          this.fx.sparks(e.x, e.y, e.dx, e.dy, 4);
          if (e.a > 0.6) this.fx.dust(e.x, e.y, 3);
        }
        break;
      }
      case EV.StructureHit: {
        if (!onScreen) break;
        this.fx.dust(e.x, e.y, 2, 0x5a5348);
        if (e.a > 30) {
          this.fx.sparks(e.x, e.y, 0, -1, 3);
          cam.shake(clamp(e.a * 0.04, 1, 6));
        }
        break;
      }
      case EV.StructureDown: {
        if (!onScreen) break;
        this.fx.smokePlume(e.x, e.y, 12, 0x3a342c);
        this.fx.dust(e.x, e.y, 14, 0x5a5348);
        this.fx.decal(this.bank.scorch, e.x, e.y, Math.sqrt(e.a) * 1.4, 0x100e0c, 0.5, 0, 600);
        cam.shake(16, 0.45);
        this.fx.freeze(0.1);
        break;
      }
      case EV.Breach: {
        this.fx.dust(e.x, e.y, 16, 0x6a6459);
        this.fx.smokePlume(e.x, e.y, 6, 0x3a342c);
        cam.shake(18, 0.5);
        cam.zoomPunch(0.04);
        this.fx.freeze(0.14);
        break;
      }
      case EV.Power: {
        this.fx.shockRing(e.x, e.y, e.a * 0.8, C.goldPale, 0.5);
        break;
      }
      case EV.Rally: {
        this.fx.shockRing(e.x, e.y, e.a, C.gold, 0.7);
        this.fx.embers(e.x, e.y, 14, 2);
        break;
      }
      case EV.Levelup: {
        this.fx.shockRing(e.x, e.y, 2.2, C.goldPale, 0.8);
        break;
      }
      case EV.Rout: {
        if (onScreen) this.fx.dust(e.x, e.y, 2, 0x4a443b);
        break;
      }
      case EV.Build: {
        if (onScreen) this.fx.dust(e.x, e.y, 5, 0x6a6459);
        break;
      }
      case EV.Spawn: {
        if (onScreen) this.fx.dust(e.x, e.y, 6, 0x4a443b);
        break;
      }
      case EV.WaveStart: {
        cam.shake(10, 0.7);
        break;
      }
      case EV.Boss: {
        const [name, title] = e.text.split('|');
        this.bossCard = { name: name ?? '', title: title ?? '', t: 5.5 };
        cam.shake(20, 1.1);
        break;
      }
      case EV.Log: {
        this.log.push({ text: e.text, tone: e.a, t: 9 });
        if (this.log.length > 60) this.log.shift();
        break;
      }
      default:
        break;
    }

    // Any hit makes the victim flash and squash.
    if (e.k === EV.Hit || e.k === EV.Block) {
      void w;
    }
  }

  private isVisible(x: number, y: number): boolean {
    const b = this.camera.viewBounds(3);
    return x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1;
  }

  // ── Frame ────────────────────────────────────────────────────────────────

  render(w: World, alpha: number, dt: number, sel: SelectionState, preview?: BuildPreview): void {
    this.time += dt;
    /**
     * Point the building bank at the people who live here.
     *
     * Nothing did this before. `TextureBank.build()` set the Ashen style and no
     * line anywhere else ever moved it, so a Karrun-Dûr settlement, a Moonbound
     * camp and a Pale Litany necropolis were all drawn in the same grey rubble
     * and the whole architecture table was dead code from the day it was
     * written. It is watched here rather than pushed from wherever a race is
     * chosen because there are now three doors — the menu, the campaign and the
     * debug console — and a renderer that reads the world it is given cannot be
     * out of step with it.
     *
     * A string compare a frame, and the bake behind it happens once per people
     * per session.
     */
    if (w.raceId !== this.vernacularId) {
      this.vernacularId = w.raceId;
      this.bank.setVernacular(w.raceId);
    }
    /**
     * And point the ground at the country it stands in.
     *
     * The same shape as the line above and for the same reason. `config.biome`
     * has decided this settlement's terrain mix since `7071729` — a mountain
     * capital is generated with 47,022 stone tiles against a forest one's 4,298
     * — and the renderer painted every one of them out of the same box.
     *
     * Watched rather than pushed, and keyed on the *seed* as well as the
     * country, because two runs in the same country are still two places and
     * every chunk resident from the last one is a picture of the wrong hill.
     */
    const key = `${w.config.biome ?? 'plain'}|${w.config.seed}`;
    if (key !== this.groundKey) {
      this.groundKey = key;
      this.theme = themeFor(w.config.biome);
      this.bank.setBiome(w.config.biome);
      for (let i = 0; i < this.chunkVersion.length; i++) this.chunkVersion[i] = -1;
    }
    this.camera.update(dt, this.time);
    const cam = this.camera;

    this.drawTerrain(w);
    this.fx.syncDecalLayer(cam);
    this.drawZones(w);
    this.drawTiles(w);
    this.drawStructures(w, dt);
    this.drawRubble(w);
    this.drawNodes(w);
    this.drawScatter(w);
    this.drawUnits(w, alpha, dt);
    this.drawProjectiles(w, alpha);
    this.drawNight(w);
    this.drawOverlay(w, sel);
    this.drawPreview(w, preview);
    this.fx.update(dt, cam);

    for (let i = this.log.length - 1; i >= 0; i--) {
      this.log[i].t -= dt;
      if (this.log[i].t <= 0) this.log.splice(i, 1);
    }
    if (this.bossCard) {
      this.bossCard.t -= dt;
      if (this.bossCard.t <= 0) this.bossCard = null;
    }
  }

  // ── Terrain ──────────────────────────────────────────────────────────────

  private drawTerrain(w: World): void {
    const cam = this.camera;
    const b = cam.viewBounds(1);
    const cx0 = Math.max(0, Math.floor(b.x0 / CHUNK));
    const cx1 = Math.min(CHUNKS_X - 1, Math.floor(b.x1 / CHUNK));
    const cy0 = Math.max(0, Math.floor(b.y0 / CHUNK));
    const cy1 = Math.min(CHUNKS_Y - 1, Math.floor(b.y1 / CHUNK));

    for (const s of this.chunkSprites) if (s) s.visible = false;

    /**
     * How many *stale* chunks may be re-baked in one frame.
     *
     * A chunk's ground is no longer 256 sprites drawn into a render target; it
     * is that plus `GroundBaker`, which is 9,216 pixels of JavaScript and
     * measures 2 ms a chunk. Nine to twelve chunks are on screen at the zoom a
     * player plays at, and `terrainVersion` is global — **one road tile
     * invalidates every one of them**.
     *
     * Measured, A/B/A/B in one session at z30: unbudgeted, the frame after a
     * terrain change costs **p50 85–115 ms and up to 218 ms** — a stall a
     * player feels as the game stopping. Budgeted at two, the same change costs
     * **p50 10–19 ms** on that frame and spreads the rest over the five or six
     * after it. (Headless swiftshader with seven other builds running, so read
     * the ratio and not the absolute.)
     *
     * Two is enough because a stale chunk is not a *missing* chunk: the sprite
     * is still there holding the previous picture, so what the player sees is
     * the far end of a road appearing two frames after the near end. A hole
     * would be a different matter, which is why a chunk with no sprite at all
     * is baked immediately and does not draw on this budget — a camera reaching
     * new ground has always paid that, and the alternative is ground that
     * flickers in as you pan.
     *
     * A chunk that misses the budget keeps its old `chunkVersion`, so it is
     * simply first in line next frame; nothing has to remember that it was
     * skipped.
     */
    let budget = 2;

    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        const ci = cy * CHUNKS_X + cx;
        let sprite = this.chunkSprites[ci];
        const stale = !!sprite && this.chunkVersion[ci] !== w.map.terrainVersion;
        // A missing chunk is baked whatever the budget says; a stale one waits
        // its turn and goes on showing the picture it already has.
        if (!sprite || (stale && budget > 0)) {
          if (stale) budget--;
          const tex = this.bakeChunk(w, cx, cy);
          if (!sprite) {
            sprite = new Sprite(tex);
            sprite.anchor.set(0, 0);
            this.terrainLayer.addChild(sprite);
            this.chunkSprites[ci] = sprite;
          } else {
            // The texture being replaced is a render target this scene owns.
            // Nothing else will free it.
            const old = sprite.texture;
            sprite.texture = tex;
            old.destroy(true);
          }
          this.chunkVersion[ci] = w.map.terrainVersion;
        }
        sprite.visible = true;
        sprite.x = cam.worldToScreenX(cx * CHUNK);
        sprite.y = cam.worldToScreenY(cy * CHUNK);
        const scale = (cam.effectiveZoom * CHUNK) / (CHUNK * ART_TILE_PX);
        sprite.scale.set(scale);
      }
    }

    this.evictChunks(cx0, cy0, cx1, cy1);
  }

  /**
   * Throw away baked terrain the camera has left behind.
   *
   * Each chunk is a 512×512 render target — a megabyte of video memory — and
   * the cache had no eviction at all. On a 128-tile map that was sixty-four
   * chunks and sixty-four megabytes if you toured the whole thing, which is
   * survivable; on a 384-tile map it is five hundred and seventy-six, and a
   * player who pans across their city once never gets any of it back. Resident
   * terrain is now bounded by what is on screen plus a margin, which is a
   * constant that does not care how large the map becomes.
   *
   * The margin matters: re-baking is real work — two hundred and fifty-six
   * tiles drawn into a render target — so a chunk that is one step off the
   * edge of the view is kept, and only a camera that has genuinely moved on
   * pays to bake it again.
   */
  private evictChunks(cx0: number, cy0: number, cx1: number, cy1: number): void {
    const keepX0 = cx0 - CHUNK_KEEP;
    const keepX1 = cx1 + CHUNK_KEEP;
    const keepY0 = cy0 - CHUNK_KEEP;
    const keepY1 = cy1 + CHUNK_KEEP;
    for (let cy = 0; cy < CHUNKS_Y; cy++) {
      const inY = cy >= keepY0 && cy <= keepY1;
      for (let cx = 0; cx < CHUNKS_X; cx++) {
        if (inY && cx >= keepX0 && cx <= keepX1) continue;
        const ci = cy * CHUNKS_X + cx;
        const sprite = this.chunkSprites[ci];
        if (!sprite) continue;
        this.terrainLayer.removeChild(sprite);
        const tex = sprite.texture;
        sprite.destroy();
        tex.destroy(true);
        this.chunkSprites[ci] = null;
        this.chunkVersion[ci] = -1;
      }
    }
  }

  /**
   * One chunk of ground: the field, then the marks on it.
   *
   * Two layers, and the split is the whole of this pass. The **field** —
   * colour, hillshade and the country's coarse grain — is computed in
   * `render/ground.ts` at six pixels to a tile and stretched over the chunk by
   * the GPU, which is what lets the edge between grass and forest floor be a
   * transition rather than a tile boundary. The **marks** are the same
   * thirty-two-pixel tile textures as before, except that they no longer carry
   * a base fill: they are tufts, pebbles, reeds and planks composited over
   * ground that already has a colour and a light.
   *
   * Both are baked into one render target and the target is the thing that is
   * cached, so the per-frame cost is unchanged — this is work at bake time,
   * which happens when the camera reaches new ground and when `terrainVersion`
   * moves, and never per frame.
   */
  private bakeChunk(w: World, cx: number, cy: number) {
    const g = new Container();
    const ground = this.bakeGround(w, cx, cy);
    if (ground) g.addChild(ground);

    for (let ty = 0; ty < CHUNK; ty++) {
      for (let tx = 0; tx < CHUNK; tx++) {
        const x = cx * CHUNK + tx;
        const y = cy * CHUNK + ty;
        if (!w.map.inBounds(x, y)) continue;
        const k = w.map.idx(x, y);
        const t = w.map.terrain[k];
        const variants = this.bank.terrain[t] ?? this.bank.terrain[Terrain.AshPlain];
        const s = new Sprite(variants[this.tileVariant(w, x, y, t, k)]);
        s.x = tx * ART_TILE_PX;
        s.y = ty * ART_TILE_PX;
        g.addChild(s);
      }
    }
    const tex = this.bank.bake(g, CHUNK * ART_TILE_PX, CHUNK * ART_TILE_PX, 1);
    // The ground image has been drawn into the target; the target is what is
    // kept. Nothing else holds this source and nothing else will free it.
    if (ground) ground.texture.destroy(true);
    return tex;
  }

  /**
   * Which of a terrain's four marks this tile wears.
   *
   * `decor` — the map's own saved per-tile variation — for everything except
   * the two surfaces that have a *run*: a bridge, whose deck has to lie across
   * the water and therefore along the road, and the road itself, whose ruts lie
   * along the direction of travel. The old bake laid both the same way in every
   * tile, so half the bridges in the game were decked lengthways — which reads
   * as a raft — and every north–south road was rung like a ladder. Two bits: the
   * low one is still the tile's own variation and the high one is the axis, so
   * both keep their variety and gain their direction for nothing.
   *
   * Read off the neighbours rather than stored, exactly like the wall masks —
   * a road is a run of tiles and a run has a direction whether or not anybody
   * wrote it down.
   */
  private tileVariant(w: World, x: number, y: number, t: number, k: number): number {
    const v = w.map.decor[k] & 3;
    if (t !== Terrain.Bridge && t !== Terrain.Road) return v;
    const map = w.map;
    const spans = (dx: number, dy: number): number => {
      let n = 0;
      for (const s of [-1, 1]) {
        const nx = x + dx * s;
        const ny = y + dy * s;
        if (!map.inBounds(nx, ny)) continue;
        const nt = map.terrain[map.idx(nx, ny)];
        if (nt === Terrain.Bridge || nt === Terrain.Road) n++;
      }
      return n;
    };
    // A deck that runs north–south is one whose *road* runs north–south.
    return (v & 1) | (spans(0, 1) > spans(1, 0) ? 2 : 0);
  }

  /**
   * The chunk's ground field as a sprite, ready to be drawn under the marks.
   *
   * A fresh canvas per bake, because Pixi's texture cache is keyed on the
   * source and reusing one canvas would hand back the previous chunk's picture.
   * The `ImageData` behind it *is* reused — 96 × 96 × 4 bytes, and a full
   * re-bake walks a dozen chunks over the next handful of frames.
   *
   * Returns null rather than throwing where a 2D context cannot be had: a
   * settlement whose ground has no shading is the settlement that shipped
   * yesterday, and one that throws is not.
   */
  private bakeGround(w: World, cx: number, cy: number): Sprite | null {
    const P = this.groundBaker.px;
    const canvas = document.createElement('canvas');
    canvas.width = P;
    canvas.height = P;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    if (!this.groundImage || this.groundImage.width !== P) {
      this.groundImage = ctx.createImageData(P, P);
    }
    this.groundBaker.bake(
      {
        terrain: w.map.terrain,
        mapW: w.map.w,
        mapH: w.map.h,
        theme: this.theme,
        seed: w.config.seed,
        // The battlefield's ridge, if this world has one. Null everywhere else.
        height: w.heightGrid,
      },
      cx,
      cy,
      this.groundImage.data,
    );
    ctx.putImageData(this.groundImage, 0, 0);
    const s = new Sprite(new Texture({ source: new CanvasSource({ resource: canvas }) }));
    s.width = CHUNK * ART_TILE_PX;
    s.height = CHUNK * ART_TILE_PX;
    return s;
  }

  // ── Ground effects ───────────────────────────────────────────────────────

  private drawZones(w: World): void {
    const cam = this.camera;
    const g = this.zoneG;
    g.clear();
    const z = cam.effectiveZoom;

    for (const zone of w.zones) {
      const sx = cam.worldToScreenX(zone.x);
      const sy = cam.worldToScreenY(zone.y);
      const r = zone.r * z;
      const t = 1 - zone.t / Math.max(0.001, zone.life);

      switch (zone.kind) {
        case ZoneKind.Strike: {
          // The telegraph. Red, unmissable, and it fills as the thing arrives.
          g.circle(sx, sy, r).fill({ color: 0x8c1f1a, alpha: 0.16 });
          g.circle(sx, sy, r).stroke({ width: 2.5, color: 0xd1352c, alpha: 0.9 });
          g.circle(sx, sy, r * t).fill({ color: 0xd1352c, alpha: 0.24 });
          g.circle(sx, sy, r * t).stroke({ width: 2, color: 0xff7a5c, alpha: 0.95 });
          for (let i = 0; i < 8; i++) {
            const a = (i / 8) * Math.PI * 2 + this.time * 0.6;
            g.moveTo(sx + Math.cos(a) * r * 0.86, sy + Math.sin(a) * r * 0.86)
              .lineTo(sx + Math.cos(a) * r, sy + Math.sin(a) * r)
              .stroke({ width: 2, color: 0xff7a5c, alpha: 0.8 });
          }
          break;
        }
        case ZoneKind.Poison:
          g.circle(sx, sy, r).fill({ color: C.plague, alpha: 0.14 });
          g.circle(sx, sy, r).stroke({ width: 2, color: C.plagueLight, alpha: 0.5 });
          break;
        case ZoneKind.Heal:
          g.circle(sx, sy, r).fill({ color: C.goldPale, alpha: 0.1 });
          g.circle(sx, sy, r).stroke({ width: 2, color: C.gold, alpha: 0.55 });
          break;
        case ZoneKind.Slow:
          g.circle(sx, sy, r).fill({ color: 0x8a6c26, alpha: 0.12 });
          g.circle(sx, sy, r).stroke({ width: 2, color: C.goldPale, alpha: 0.4 });
          break;
        case ZoneKind.Fire:
          g.circle(sx, sy, r).fill({ color: C.emberDeep, alpha: 0.13 });
          g.circle(sx, sy, r).stroke({ width: 2, color: C.ember, alpha: 0.5 });
          break;
        case ZoneKind.Sunder:
          g.circle(sx, sy, r).fill({ color: 0xbfc4ae, alpha: 0.1 });
          g.circle(sx, sy, r).stroke({ width: 1.6, color: 0xd8dcc6, alpha: 0.45 });
          break;
        case ZoneKind.Shield:
        case ZoneKind.Rally:
          g.circle(sx, sy, r).stroke({ width: 2, color: C.gold, alpha: 0.4 });
          break;
        default:
          break;
      }
    }
  }

  // ── Walls, gates, rubble ─────────────────────────────────────────────────

  private drawTiles(w: World): void {
    const cam = this.camera;
    const b = cam.viewBounds(2);
    const x0 = Math.max(0, Math.floor(b.x0));
    const x1 = Math.min(MAP_W - 1, Math.ceil(b.x1));
    const y0 = Math.max(0, Math.floor(b.y0));
    const y1 = Math.min(MAP_H - 1, Math.ceil(b.y1));
    const z = cam.effectiveZoom;
    let n = 0;
    this.curtainG.clear();
    this.markG.clear();

    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const k = w.map.idx(tx, ty);
        const wall = w.map.wall[k];
        const rubble = w.map.rubble[k];
        if (wall === WallKind.None && rubble === 0) continue;

        // The connective tissue, drawn over the baked tile — see `wallBody`.
        if (wall === WallKind.Wall) this.wallBody(w, tx, ty, k, z);
        else if (wall === WallKind.Tower) this.towerBody(w, tx, ty, z);
        else if (wall === WallKind.Gate) this.gateHouse(w, tx, ty, k, z);

        const s = this.tileSprite(n++);
        s.x = cam.worldToScreenX(tx);
        s.y = cam.worldToScreenY(ty);
        s.width = z;
        s.height = z;
        s.visible = true;

        if (wall === WallKind.Gate) {
          const vertical =
            (ty > 0 && w.map.wall[w.map.idx(tx, ty - 1)] !== WallKind.None) ||
            (ty < MAP_H - 1 && w.map.wall[w.map.idx(tx, ty + 1)] !== WallKind.None);
          s.texture = this.bank.walls[vertical ? 'gateV' : 'gateH'];
        } else if (wall !== WallKind.None) {
          let mask = 0;
          if (ty > 0 && w.map.wall[w.map.idx(tx, ty - 1)] !== WallKind.None) mask |= 1;
          if (tx < MAP_W - 1 && w.map.wall[w.map.idx(tx + 1, ty)] !== WallKind.None) mask |= 2;
          if (ty < MAP_H - 1 && w.map.wall[w.map.idx(tx, ty + 1)] !== WallKind.None) mask |= 4;
          if (tx > 0 && w.map.wall[w.map.idx(tx - 1, ty)] !== WallKind.None) mask |= 8;
          s.texture = this.bank.walls[`w${mask}`];
        } else {
          s.texture = this.bank.walls.rubble;
        }

        if (wall !== WallKind.None) {
          // Damage reads as the stone going dark and ragged.
          const hp = w.map.wallMaxHp[k] > 0 ? w.map.wallHp[k] / w.map.wallMaxHp[k] : 1;
          s.tint = hp > 0.95 ? 0xffffff : mix(0x6b4a44, 0xffffff, clamp01(hp));
          s.alpha = 1;
        } else {
          s.tint = 0xffffff;
          s.alpha = 0.85;
        }
      }
    }
    for (let i = n; i < this.tileSprites.length; i++) this.tileSprites[i].visible = false;

    this.drawCurtain(w, x0, y0, x1, y1);
  }

  /**
   * The people whose masonry this is.
   *
   * Resolved through a one-entry cache rather than per tile: `vernacularOf`
   * walks a table and a siege draws several hundred wall tiles a frame, and the
   * answer changes when the world does and at no other time.
   */
  private vernFor(w: World): Vernacular {
    if (this.vernId !== w.raceId || !this.vern) {
      this.vernId = w.raceId;
      this.vern = vernacularOf(w.raceId);
    }
    return this.vern;
  }

  /**
   * One tile of curtain, in its tier's own material.
   *
   * The drawing is in `curtainart.ts`; what lives here is the question the art
   * cannot ask — which neighbours are stone, and how hurt this stretch is. The
   * arms reach a hair past the tile edge so two neighbours overlap instead of
   * leaving a seam at fractional zooms, which is what the sixteen baked
   * auto-tile masks were meant to do and never did: every arm they draw is
   * placed outside the texture's own bounds and clipped away by the bake, so
   * only the 56% core ever survived and a wall rendered as a row of separate
   * blocks with daylight between them.
   */
  private wallBody(w: World, tx: number, ty: number, k: number, z: number): void {
    const cam = this.camera;
    const wall = w.map.wall;
    const frac = w.map.wallMaxHp[k] > 0 ? clamp01(w.map.wallHp[k] / w.map.wallMaxHp[k]) : 1;
    drawWallTile(
      this.curtainG,
      cam.worldToScreenX(tx),
      cam.worldToScreenY(ty),
      z,
      /**
       * `wallTier` is **1-based**, the same numbering `wallTierDef` indexes with
       * and the same one a player reads: 1 is a palisade, 3 is a bastion.
       *
       * The first cut of this passed `tier + 1` on the assumption it was a
       * 0-based store, which shifted every stretch of curtain in the game up one
       * material — a fresh palisade rendered as ashlar, and there was no way to
       * see a palisade at all. The contact sheet caught it in one look.
       */
      w.map.wallTier[k],
      frac,
      ty > 0 && wall[k - MAP_W] !== WallKind.None,
      tx < MAP_W - 1 && wall[k + 1] !== WallKind.None,
      ty < MAP_H - 1 && wall[k + MAP_W] !== WallKind.None,
      tx > 0 && wall[k - 1] !== WallKind.None,
      this.vernFor(w),
    );
  }

  /** The piers that give a gate a silhouette. See `drawGatehouse`. */
  private gateHouse(w: World, tx: number, ty: number, k: number, z: number): void {
    const cam = this.camera;
    const frac = w.map.wallMaxHp[k] > 0 ? clamp01(w.map.wallHp[k] / w.map.wallMaxHp[k]) : 1;
    // Piers stand across the run, so a gate in a north–south wall gets them
    // east and west of it.
    const vertical =
      (ty > 0 && w.map.wall[k - MAP_W] !== WallKind.None) ||
      (ty < MAP_H - 1 && w.map.wall[k + MAP_W] !== WallKind.None);
    drawGatehouse(
      this.curtainG,
      cam.worldToScreenX(tx),
      cam.worldToScreenY(ty),
      z,
      vertical,
      frac,
      this.vernFor(w),
    );
  }

  /**
   * A tower, drawn from the top-left tile of its block so a 2×2 is one body
   * rather than four stubs.
   *
   * Its health is the average of the block's four tiles: they are hit
   * individually and a tower that showed the worst of them would flicker
   * between two colours as the ram walked along it.
   */
  private towerBody(w: World, tx: number, ty: number, z: number): void {
    if (tx > 0 && w.map.wall[w.map.idx(tx - 1, ty)] === WallKind.Tower) return;
    if (ty > 0 && w.map.wall[w.map.idx(tx, ty - 1)] === WallKind.Tower) return;

    let hp = 0;
    let max = 0;
    for (let dy = 0; dy < TOWER_SIZE; dy++) {
      for (let dx = 0; dx < TOWER_SIZE; dx++) {
        if (!w.map.inBounds(tx + dx, ty + dy)) continue;
        const kk = w.map.idx(tx + dx, ty + dy);
        hp += w.map.wallHp[kk];
        max += w.map.wallMaxHp[kk];
      }
    }
    const cam = this.camera;
    drawTower(
      this.curtainG,
      cam.worldToScreenX(tx),
      cam.worldToScreenY(ty),
      z,
      TOWER_SIZE,
      max > 0 ? clamp01(hp / max) : 1,
      this.vernFor(w),
    );
  }

  /**
   * The two things about a circuit that no amount of stonework can say: that
   * there is a hole in it, and that it does not close.
   *
   * Drawn above the men rather than under them, because a breach with a pile of
   * bodies on it is exactly the moment the mark has to still be visible.
   */
  private drawCurtain(w: World, x0: number, y0: number, x1: number, y1: number): void {
    const cam = this.camera;
    const z = cam.effectiveZoom;
    const m = this.markG;

    // ── Breaches ──────────────────────────────────────────────────────────
    //
    // Always drawn, siege or not. A hole in the line is the single most
    // important thing on the screen the moment it exists, and it stays until
    // somebody fills it in — this is geometry, not a fading effect.
    const reading = this.curtain.update(w.map);
    const pulse = 0.55 + 0.45 * Math.sin(this.time * 4.4);
    /**
     * Marks have a floor in *pixels*, not in tiles.
     *
     * The zoom a player uses to ask "is my ring closed" is the one where the
     * whole ring fits on the screen, which is also the zoom at which a tile is
     * eleven pixels across. A mark sized in tiles disappears at exactly the
     * moment it is being looked for.
     */
    const markR = Math.max(7, z * 0.46);
    const markW = Math.max(2, z * 0.06);
    for (const k of reading.breaches) {
      const tx = k % MAP_W;
      const ty = (k / MAP_W) | 0;
      if (tx < x0 || tx > x1 || ty < y0 || ty > y1) continue;
      const sx = cam.worldToScreenX(tx);
      const sy = cam.worldToScreenY(ty);
      const cx = sx + z * 0.5;
      const cy = sy + z * 0.5;
      m.rect(sx, sy, z, z).fill({ color: C.oxblood, alpha: 0.16 + 0.12 * pulse });
      m.moveTo(cx, cy - markR * 0.85)
        .lineTo(cx + markR * 0.8, cy + markR * 0.7)
        .lineTo(cx - markR * 0.8, cy + markR * 0.7)
        .closePath()
        .stroke({ width: markW, color: C.oxbloodLight, alpha: 0.5 + 0.4 * pulse });
      m.rect(cx - markW * 0.5, cy - markR * 0.28, markW, markR * 0.6).fill({
        color: C.emberLight,
        alpha: 0.85,
      });
      m.rect(cx - markW * 0.5, cy + markR * 0.44, markW, markW).fill({
        color: C.emberLight,
        alpha: 0.85,
      });
    }

    // ── Where the ring does not close ─────────────────────────────────────
    //
    // A closed loop has no dangling ends, so "there are ends" and "the circuit
    // is open" are the same statement — which is why one mark answers both. The
    // Hearth gets a ring of its own when the flood from the map edge can reach
    // it, because a player who has walled three sides of a settlement can see
    // every stone standing and still not be enclosed.
    if (!this.showCurtain) return;
    for (const k of reading.ends) {
      const tx = k % MAP_W;
      const ty = (k / MAP_W) | 0;
      if (tx < x0 || tx > x1 || ty < y0 || ty > y1) continue;
      const sx = cam.worldToScreenX(tx) + z * 0.5;
      const sy = cam.worldToScreenY(ty) + z * 0.5;
      m.circle(sx, sy, markR).stroke({
        width: markW,
        color: C.gold,
        alpha: 0.4 + 0.45 * pulse,
      });
      m.circle(sx, sy, markR * 0.3).fill({ color: C.goldPale, alpha: 0.6 + 0.35 * pulse });
    }
    if (reading.tiles > 0 && !reading.sealed) {
      const hx = cam.worldToScreenX(w.map.hearthX + 0.5);
      const hy = cam.worldToScreenY(w.map.hearthY + 0.5);
      const r = Math.max(26, z * 2.4) * (1 + 0.06 * pulse);
      m.circle(hx, hy, r).stroke({
        width: Math.max(2, z * 0.06),
        color: C.oxbloodLight,
        alpha: 0.35 + 0.3 * pulse,
      });
      // A short dashed skirt, so an open circuit reads as a warning rather than
      // as one more circle on a screen that already has several.
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + this.time * 0.5;
        m.moveTo(hx + Math.cos(a) * r * 1.1, hy + Math.sin(a) * r * 1.1)
          .lineTo(hx + Math.cos(a) * r * 1.3, hy + Math.sin(a) * r * 1.3)
          .stroke({ width: Math.max(2, z * 0.06), color: C.oxbloodLight, alpha: 0.45 });
      }
    }
  }

  private tileSprite(i: number): Sprite {
    let s = this.tileSprites[i];
    if (!s) {
      s = new Sprite(this.bank.walls.w0);
      s.anchor.set(0, 0);
      this.structureLayer.addChild(s);
      this.tileSprites[i] = s;
    }
    return s;
  }

  // ── Buildings ────────────────────────────────────────────────────────────

  private drawStructures(w: World, dt: number): void {
    const cam = this.camera;
    const z = cam.effectiveZoom;
    const b = cam.viewBounds(6);
    const bs = w.buildings;
    let n = 0;

    for (let i = 0; i < bs.slots.high; i++) {
      if (bs.slots.alive[i] === 0) continue;
      const def = buildingType(bs.typeId[i]);
      const tx = bs.tx[i];
      const ty = bs.ty[i];
      if (tx > b.x1 || ty > b.y1 || tx + def.w < b.x0 || ty + def.h < b.y0) continue;

      const s = this.structureSprite(n++);
      // A dropped-in still (spritedrop.ts) wins over the baked vector; its
      // width is stretched to span the footprint whatever its resolution.
      /**
       * A hurt building is a different picture, not the same one reddened.
       *
       * The wreck still wins over the vernacular drop-in, which is deliberate:
       * a people's own architecture is what is *standing*, and once it is not
       * standing the interesting fact is that it came down. See `wreckart.ts`
       * for the compromise that costs.
       */
      const built0 = bs.build[i];
      // `wreckArt` returns null for a building healthy enough to be drawn as
      // itself, which is nearly all of them nearly all the time — so the id is
      // tested before it is asked for. Passing `''` through instead probes
      // `art/props/.png`, which is a 404 on the first healthy building on every
      // map and was exactly that until the shot harness reported it.
      const wreckId = built0 >= 1 ? wreckArt(def.vis.shape, bs.hp[i] / Math.max(1, bs.maxHp[i])) : null;
      const hurt = wreckId === null ? null : propTexture(wreckId);
      const drop = hurt ?? buildingArt(this.bank.vernacularId, def.id);
      s.texture = drop ?? this.bank.buildings[bs.typeId[i]];
      s.visible = true;
      // Textures carry headroom above the footprint; anchor at the bottom-left.
      s.anchor.set(0, 1);
      s.x = cam.worldToScreenX(tx);
      s.y = cam.worldToScreenY(ty + def.h);
      const scale = drop ? (z * def.w) / drop.width : z / ART_TILE_PX;
      s.scale.set(scale);

      const built = bs.build[i];
      const hpFrac = bs.hp[i] / Math.max(1, bs.maxHp[i]);
      if (built < 1) {
        s.alpha = 0.3 + built * 0.6;
        s.tint = mix(0x6a7a8a, 0xffffff, built);
      } else {
        s.alpha = 1;
        // Damage darkens and reddens the stone — but only while the *shape* is
        // still whole. Once a wreck still is carrying the damage, tinting it as
        // well doubles the cue and turns a legible ruin into a red smear.
        s.tint =
          hurt !== null || hpFrac > 0.98
            ? 0xffffff
            : mix(0x7a4038, 0xffffff, clamp01(hpFrac * 0.6 + 0.4));
      }

      // Buildings on fire smoke.
      if (built >= 1 && hpFrac < 0.6 && this.time % 0.35 < 0.02) {
        this.fx.smokePlume(tx + def.w / 2, ty + def.h / 2, 1, 0x2f2a24);
      }

      /**
       * A working chimney smokes. This is the cheapest liveliness in the game
       * and the one that carries furthest: at the zoom where a villager is a
       * dot, a row of smoking roofs is still a town where somebody is up.
       *
       * Gated three ways so it stays cheap. Only buildings that *have* a stack —
       * the motif is the authority, so a bakery smokes and a drill yard does not
       * — only when somebody is actually working there, and only on the
       * building's own phase, so of the thirty or so roofs on screen at default
       * zoom about one puffs per frame rather than all of them.
       */
      const m = def.vis.motif;
      const lit = bs.workers[i] > 0 || (def.popCap > 0 && w.pop > 0);
      if (
        built >= 1 &&
        lit &&
        (m === 'chimney' || m === 'chimneys' || m === 'still') &&
        (this.time * 0.6 + i * 0.113) % 1 < dt * 0.6
      ) {
        this.fx.smokePlume(tx + def.w * 0.72, ty + def.h * 0.3, 0.55, 0x4a443a);
      }
    }
    for (let i = n; i < this.structureSprites.length; i++) this.structureSprites[i].visible = false;
  }

  private structureSprite(i: number): Sprite {
    let s = this.structureSprites[i];
    if (!s) {
      s = new Sprite();
      this.structureLayer.addChild(s);
      this.structureSprites[i] = s;
    }
    return s;
  }

  // ── Units ────────────────────────────────────────────────────────────────

  /**
   * The trees and rocks a field battle is fought around.
   *
   * Drawn into `structureLayer`, so men pass in front of them exactly as they
   * pass in front of a building. That is a real limitation and it is inherited
   * rather than chosen: this renderer sorts by layer and not by depth, so
   * nothing in the game has ever gone *behind* a structure. A tree is the first
   * thing where that reads oddly, because a tree is small enough that a player
   * expects to walk round the back of it — worth fixing when structures get a
   * depth sort, and not worth a second sorted layer for scatter alone.
   *
   * **The list is cached and not re-read per frame.** `fieldObstacles` hands
   * out copies on purpose, which is right for a reader and wrong to call sixty
   * times a second; the scatter is rolled once at `createBattle` and never
   * changes after, so the cache is keyed on the battle's own object identity
   * and its length. It is dropped the moment a battle ends, so a settlement
   * carries nothing.
   */
  /**
   * The wood, the stone and the ore, standing where the simulation says.
   *
   * Culled every frame rather than cached, unlike the battlefield scatter, and
   * the difference is real: a battle's obstacles are rolled once and never
   * change, but a settlement's nodes are **worked down and removed** — that is
   * the whole point of them — so a held list would draw a tree somebody felled
   * two minutes ago. `nodesInBox` is one walk of the store per frame against
   * roughly thirteen thousand nodes on a map, which is the same order as the
   * building walk `stepProduction` already does every tick.
   *
   * Drawn into `structureLayer` with the buildings, so a villager passes in
   * front of a tree exactly as he passes in front of a house — the same
   * inherited flat-sorting limitation `drawScatter` documents.
   */
  private drawNodes(w: World): void {
    const cam = this.camera;
    const z = cam.effectiveZoom;
    const b = cam.viewBounds(3);
    let n = 0;

    nodesInBox(w.nodes, b.x0, b.y0, b.x1, b.y1, this.nodeVisible);

    for (const i of this.nodeVisible) {
      const r = nodeReadout(w.nodes, i);
      if (r === null) continue;
      const tex = propTexture(nodeArt(r.kind, w.nodes.tile[i], r.stage));
      if (tex === null) continue;

      let s = this.nodeSprites[n];
      if (s === undefined) {
        s = new Sprite();
        s.anchor.set(0.5, NODE_GROUND);
        this.structureLayer.addChild(s);
        this.nodeSprites[n] = s;
      }
      n++;

      const wide = nodeSize(r.kind) * z;

      /**
       * The shadow it throws, and this is what seats it on the ground.
       *
       * Every man in this game has stood in `shadowLayer` since the renderer
       * was written — *"the shadow is unmistakably standing on something, at
       * every zoom"* is the note at `drawUnits` — and the wood and the stone
       * never did. At close zoom a stand of trees read as cut-outs pasted over
       * the ash, which is exactly the fault the archers-hovering-above-the-wall
       * pass was about, in the one place nobody had looked.
       *
       * The same `bank.blob` as a soldier's, at the same layer and the same
       * 0.42, offset **south-east**: the light in this game comes from the
       * north-west and has done since `drawBuilding`, so the shadow goes the
       * other way. Sized off the prop rather than the tile, because a 2.3-tile
       * tree and a 1.0-tile ore seam are not the same object.
       */
      const sh = this.nodeShadow(n - 1);
      sh.texture = this.bank.blob;
      sh.tint = 0x000000;
      sh.width = wide * 0.62;
      sh.height = wide * 0.3;
      sh.x = cam.worldToScreenX(r.x) + wide * 0.13;
      sh.y = cam.worldToScreenY(r.y) + wide * 0.06;
      sh.visible = true;

      s.texture = tex;
      /**
       * Seated in the country it stands in.
       *
       * The tree, rock and ore stills were baked once, against ash, and there
       * is one set for the whole game — a Litany rock is a rock, and baking
       * eleven countries' worth of boulder is not a trade worth making. A
       * multiply tint is: it cannot brighten, so it can only ever pull the
       * prop toward the ground it is standing on, which is exactly the job.
       * A wood in the snow comes out cold and pale, one in a jungle green.
       */
      s.tint = this.theme.prop;
      s.width = wide;
      s.height = wide;
      s.x = cam.worldToScreenX(r.x);
      s.y = cam.worldToScreenY(r.y);
      s.visible = true;
    }

    for (let i = n; i < this.nodeSprites.length; i++) this.nodeSprites[i].visible = false;
    for (let i = n; i < this.nodeShadows.length; i++) this.nodeShadows[i].visible = false;
  }

  /** A pooled shadow for the `i`-th visible node. See `drawNodes`. */
  private nodeShadow(i: number): Sprite {
    let s = this.nodeShadows[i];
    if (s === undefined) {
      s = new Sprite();
      s.anchor.set(0.5, 0.5);
      this.shadowLayer.addChild(s);
      this.nodeShadows[i] = s;
    }
    return s;
  }

  /**
   * The piles left where a building came down.
   *
   * `map.rubble[k]` is `buildingTypeIndex + 1`, not a boolean — the tile
   * remembers *what stood there* so the player can put it back with one click,
   * and that is exactly what lets the pile be drawn as the right kind of wreck
   * rather than as generic debris.
   *
   * Only the **origin** tile of a footprint gets a sprite. A 3×2 hall leaves
   * six rubble tiles and one pile: drawing a full-sized wreck on each would
   * stack six overlapping copies of the same picture. The origin is found the
   * way the ground itself remembers it — the tile whose neighbours up and left
   * carry a different mark — so nothing has to be stored.
   *
   * The whole pass is skipped when `w.rubbleCount` is zero, which is every
   * settlement that has not been hit yet and every battlefield. That counter
   * exists precisely so idle villagers can skip the same scan.
   */
  private drawRubble(w: World): void {
    if (w.rubbleCount === 0) {
      for (const s of this.rubbleSprites) s.visible = false;
      return;
    }

    const cam = this.camera;
    const z = cam.effectiveZoom;
    const b = cam.viewBounds(2);
    const map = w.map;
    const x0 = Math.max(0, Math.floor(b.x0));
    const y0 = Math.max(0, Math.floor(b.y0));
    const x1 = Math.min(MAP_W - 1, Math.ceil(b.x1));
    const y1 = Math.min(MAP_H - 1, Math.ceil(b.y1));
    let n = 0;

    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const mark = map.rubble[y * MAP_W + x];
        if (mark === 0) continue;
        // Origin only: skip anything whose neighbour above or to the left is
        // part of the same pile.
        if (x > 0 && map.rubble[y * MAP_W + x - 1] === mark) continue;
        if (y > 0 && map.rubble[(y - 1) * MAP_W + x] === mark) continue;

        const def = buildingType(mark - 1);
        const tex = propTexture(rubbleArt(def.vis.shape));
        if (tex === null) continue;

        let s = this.rubbleSprites[n];
        if (s === undefined) {
          s = new Sprite();
          s.anchor.set(0.5, 0.82);
          this.decalLayer.addChild(s);
          this.rubbleSprites[n] = s;
        }
        n++;

        const wide = Math.max(def.w, def.h) * z * 1.15;
        s.texture = tex;
        // The same country tint as the standing wood and stone. A pile of
        // rubble in a snowfield has snow on it.
        s.tint = this.theme.prop;
        s.width = wide;
        s.height = wide;
        s.x = cam.worldToScreenX(x + def.w / 2);
        s.y = cam.worldToScreenY(y + def.h);
        s.visible = true;
      }
    }

    for (let i = n; i < this.rubbleSprites.length; i++) this.rubbleSprites[i].visible = false;
  }

  /** A pooled contact shadow for the `i`-th visible piece of scatter. */
  private scatterShadow(i: number): Sprite {
    let s = this.scatterShadows[i];
    if (s === undefined) {
      s = new Sprite();
      s.anchor.set(0.5, 0.5);
      this.shadowLayer.addChild(s);
      this.scatterShadows[i] = s;
    }
    return s;
  }

  private drawScatter(w: World): void {
    const list = w.battle?.obstacles;
    if (list === undefined || list.length === 0) {
      for (const s of this.scatterSprites) s.visible = false;
      for (const s of this.scatterShadows) s.visible = false;
      this.scatterOf = null;
      return;
    }

    if (this.scatterOf !== list) {
      this.scatterOf = list;
      this.scatterList = fieldObstacles(w);
    }

    const cam = this.camera;
    const z = cam.effectiveZoom;
    const b = cam.viewBounds(6);
    let n = 0;

    for (let i = 0; i < this.scatterList.length; i++) {
      const o = this.scatterList[i];
      if (o.x < b.x0 || o.x > b.x1 || o.y < b.y0 || o.y > b.y1) continue;
      const tex = propTexture(obstacleArt(o, i));
      if (tex === null) continue;

      let s = this.scatterSprites[n];
      if (s === undefined) {
        s = new Sprite();
        s.anchor.set(0.5, PROP_GROUND);
        this.structureLayer.addChild(s);
        this.scatterSprites[n] = s;
      }
      n++;

      const wide = o.r * PROP_SPREAD * z;

      /**
       * The shadow it throws, which is what seats it on the ash.
       *
       * The settlement's wood and stone got this when the ground was rebuilt —
       * *"at close zoom a stand of trees read as cut-outs pasted over the
       * ash"* — and the **battlefield's** scatter was the one place left
       * without it, which is exactly where it matters most, because a field has
       * nothing else standing on it. Same `bank.blob` and the same
       * `shadowLayer`, whose own alpha is the 0.42 every shadow in the game
       * shares — deliberately *not* set again here, since a sprite alpha on top
       * of a layer alpha multiplies and would have made the field's shadows
       * half the weight of the town's. Offset south-east: the light comes from
       * the north-west and has done since `drawBuilding`. Sized off the prop's
       * own radius rather than the tile, because a boulder at r = 1.5 and a
       * copse at r = 5.4 are not the same object.
       */
      const sh = this.scatterShadow(n - 1);
      sh.texture = this.bank.blob;
      sh.tint = 0x000000;
      sh.width = wide * 0.62;
      sh.height = wide * 0.3;
      sh.x = cam.worldToScreenX(o.x) + wide * 0.13;
      sh.y = cam.worldToScreenY(o.y) + wide * 0.06;
      sh.visible = true;

      s.texture = tex;
      // Battlefield scatter belongs to the field it is scattered on, same as
      // the settlement's wood and stone.
      s.tint = this.theme.prop;
      s.width = wide;
      s.height = wide;
      s.x = cam.worldToScreenX(o.x);
      s.y = cam.worldToScreenY(o.y);
      s.visible = true;
    }

    for (let i = n; i < this.scatterSprites.length; i++) this.scatterSprites[i].visible = false;
    for (let i = n; i < this.scatterShadows.length; i++) this.scatterShadows[i].visible = false;
  }

  private drawUnits(w: World, alpha: number, dt: number): void {
    const cam = this.camera;
    const z = cam.effectiveZoom;
    const b = cam.viewBounds(2);
    const u = w.units;
    const token = z < ZOOM_TOKEN_THRESHOLD;
    let n = 0;

    /**
     * Bubbles are off until the men are people again.
     *
     * `ZOOM_TOKEN_THRESHOLD` is where a soldier becomes a war-map token, and a
     * sentence over a token is a sentence over a dot. The margin above it is
     * there so the two transitions do not land on the same wheel click —
     * chatter fading out a moment before the bodies do reads as the camera
     * pulling back, and both snapping together reads as a bug.
     */
    const chatting = z >= ZOOM_TOKEN_THRESHOLD * 1.35 && w.phase !== Phase.Siege;
    this.chatter.begin();

    for (let i = 0; i < u.slots.high; i++) {
      if (u.slots.alive[i] === 0) continue;

      const px = this.prevValid[i] ? lerp(this.prevX[i], u.x[i], alpha) : u.x[i];
      const py = this.prevValid[i] ? lerp(this.prevY[i], u.y[i], alpha) : u.y[i];
      if (px < b.x0 || px > b.x1 || py < b.y0 || py > b.y1) continue;

      const t = unitType(u.typeId[i]);
      const teamIdx = u.team[i] === Team.Player ? 0 : 1;
      const art = this.bank.units[u.typeId[i]][teamIdx];
      const sx = cam.worldToScreenX(px);
      const groundY = cam.worldToScreenY(py);
      /**
       * A man on the wall is drawn **on** the wall.
       *
       * The body is lifted; the shadow and the team disc are not. That gap is
       * the entire read — a soldier floating a third of a tile above his own
       * shadow is unmistakably standing on something, at every zoom, with no
       * icon and no legend. He is lifted only once he is actually up there, so
       * the moment of climbing is visible too. A tower is taller than a wall and
       * lifts him further, which is what makes a tower look like a tower with
       * men on it rather than a thick bit of wall.
       */
      const post = u.wallTile[i];
      let lift = 0;
      if (post >= 0) {
        const ptx = Math.floor(u.x[i]);
        const pty = Math.floor(u.y[i]);
        if (pty * MAP_W + ptx === post) {
          lift = z * (w.map.wall[post] === WallKind.Tower ? TOWER_RISE : WALL_RISE);
        }
      }
      const sy = groundY - lift;

      // Facing interpolates the short way round.
      const pf = this.prevValid[i] ? this.prevFacing[i] : u.facing[i];
      const facing = pf + wrapAngle(u.facing[i] - pf) * alpha;

      // Squash-and-stretch decay from the last hit.
      if (w.tick - u.lastHitTick[i] <= 1) this.squash[i] = 1;
      this.squash[i] = Math.max(0, this.squash[i] - dt * 5.5);
      const sq = this.squash[i];

      const idx = n++;
      const shadow = this.unitShadow(idx);
      const disc = this.unitDisc(idx);
      const body = this.unitBody(idx);
      const weapon = this.unitWeapon(idx);

      // Team disc: always on, at every zoom, for every soldier.
      const mine = u.team[i] === Team.Player;
      disc.visible = true;
      disc.x = sx;
      disc.y = groundY + z * 0.08;
      const discR = z * t.radius * (u.flags[i] & (UF.Large | UF.Champion) ? 3.4 : 2.6);
      disc.width = discR;
      disc.height = discR * 0.66;
      disc.tint = mine ? 0x6d4f1c : 0x243a63;
      disc.alpha = u.state[i] === UnitState.Routing ? 0.4 : 1;

      if (token) {
        // War-map mode: shape says role, tint says side.
        const ti = tokenIndexFor(u.flags[i], t.vis.build);
        body.texture = this.bank.tokens[ti];
        body.visible = true;
        body.x = sx;
        body.y = sy;
        body.rotation = ti === 2 ? facing : 0;
        // Tokens never dip below a readable size, however far out you pull.
        const tokenScale = Math.max(0.26, z / 40) * (u.flags[i] & (UF.Large | UF.Champion) ? 1.5 : 1);
        body.scale.set(tokenScale);
        body.alpha = 1;
        // At war-map range, side is the only thing that matters. Warm is yours,
        // cold is theirs, and the faction accent is only a slight bias on that —
        // mixing them evenly made two hostile factions look like each other.
        const fac = faction(t.faction === 'common' ? w.faction.id : t.faction);
        body.tint =
          u.team[i] === Team.Player
            ? mix(C.goldPale, fac.accent, 0.3)
            : mix(0x8fa6d8, fac.accent, 0.28);
        weapon.visible = false;
        shadow.visible = false;
        continue;
      }

      // Shadow.
      shadow.visible = true;
      shadow.texture = this.bank.blob;
      shadow.x = sx;
      shadow.y = groundY + z * 0.1;
      shadow.width = z * t.radius * 2.2 * t.vis.scale;
      shadow.height = z * t.radius * 1.4 * t.vis.scale;
      shadow.tint = 0x000000;

      /**
       * The decoration half of the villager life cycle.
       *
       * `systems/villagers.ts` decides where a man goes, because a position is
       * hashed state and always was. What he *looks like* having got there is
       * this, and it is here for the reason the rule in `COORDINATION.md` gives:
       * nothing below feeds back into the world, so none of it is state, none of
       * it is saved, and turning it off changes no outcome — which is the test
       * for whether something belongs in a renderer.
       *
       * Three poses, and they are chosen to be different *shapes* rather than
       * different tints: a man swinging a tool rises and falls, a man at prayer
       * is short and wide, a pair talking lean toward each other. All three are
       * legible at a tile and a half, which is where a villager stops being a
       * person and starts being a dot.
       *
       * Cost is one `errandOf` — a hash and four compares — per villager *on
       * screen*, which the view cull has already bounded to a few hundred. The
       * nineteen thousand off screen pay nothing.
       */
      let poseW = 1;
      let poseH = 1;
      let poseLift = 0;
      // Hoisted out of the pose block: the sheet picker below wants the same
      // errand, and asking `errandOf` twice for one villager in the hottest
      // loop in the game is two hashes where one will do.
      const villager = (u.flags[i] & UF.Villager) !== 0;
      const errand = villager ? errandOf(w, i) : Errand.Work;
      if (villager) {
        const beat = this.time * 3.1 + i * 0.7;
        if (u.state[i] === UnitState.Working) {
          // The swing. Down on the stroke, and a shade wider at the bottom of it.
          const s2 = Math.sin(beat);
          poseLift = -s2 * z * 0.07;
          poseH = 1 - s2 * 0.07;
          poseW = 1 + s2 * 0.05;
        } else if (hasArrived(w, i)) {
          if (errand === Errand.Pray) {
            // Knelt. Two thirds the height and a little wider, which is a
            // different silhouette rather than a smaller one.
            poseH = 0.7;
            poseW = 1.1;
            poseLift = z * 0.06;
          } else if (errand === Errand.Talk) {
            poseLift = Math.sin(beat * 0.42) * z * 0.03;
            poseW = 1 + Math.sin(beat * 0.42) * 0.05;
          } else if (errand === Errand.Rest) {
            poseH = 0.9;
          }
        }

        /**
         * And the line over his head, on the same errand the pose came from.
         *
         * Gated on *arrived or working* for the same reason the poses are: a
         * man still walking to the well has not got there to talk about it.
         * `bubbleFor` rejects roughly half of everybody on its first hash, so
         * the common case is one multiply and a compare on top of the
         * `errandOf` that was going to run anyway.
         */
        if (chatting && (u.state[i] === UnitState.Working || hasArrived(w, i))) {
          const mood = moodOf(errand, u.state[i], u.carry[i] > 0);
          const bub = mood === null ? null : bubbleFor(w, i, mood);
          if (bub !== null) this.chatter.push(sx, sy - z * 0.5, bub);
        }
      }

      // Body: a dropped-in sprite sheet when one exists for this type
      // (spritedrop.ts), the baked vector otherwise. The sheet has already
      // turned — its columns are the facings — so it draws unrotated, and the
      // walk cycle comes from its rows, clocked only while the unit moves.
      // The weapon sprite below rides over either body unchanged.
      const sheet = villager
        ? villagerSheet(i, errand, u.state[i], u.carry[i], u.carryKind[i])
        : unitSheet(t.id);
      // Authored at ART_UNIT_PX for a soldier occupying roughly a third of the
      // texture; this factor puts that soldier at about three quarters of a
      // tile. Baked sheets fill ~85% of their cell, so they carry a
      // compensating factor toward the same on-screen size.
      let base = (z / ART_UNIT_PX) * 1.9;
      if (sheet) {
        const step =
          u.state[i] === UnitState.Moving ? Math.floor(this.time * 9) % sheet.frames : 0;
        body.texture = sheet.cells[sheetDir(facing, sheet.dirs)][step];
        body.rotation = 0;
        // The vector path bakes vis.scale into the texture; a sheet cell is
        // authored at one size, so the type's scale rides the draw instead.
        base *= 0.42 * t.vis.scale;
      } else {
        body.texture = art.body;
        body.rotation = facing;
      }
      body.visible = true;
      body.x = sx;
      body.y = sy + poseLift;
      body.scale.set(base * (1 + sq * 0.16) * poseW, base * (1 - sq * 0.14) * poseH);
      body.alpha = u.state[i] === UnitState.Routing ? 0.78 : 1;

      // White flash on the frame the blow lands.
      const sinceHit = w.tick - u.lastHitTick[i];
      body.tint = sinceHit <= 1 ? 0xffffff : sinceHit <= 3 ? 0xffdcc8 : 0xffffff;
      if (u.state[i] === UnitState.Routing) body.tint = 0x9aa4b4;
      if (u.stunT[i] > 0) body.tint = 0xa8b6d8;
      if (u.poisonT[i] > 0) body.tint = mix(0xffffff, C.plagueLight, 0.5);
      if (u.rallyT[i] > 0) body.tint = mix(body.tint, C.goldPale, 0.35);

      // Weapon: windup → contact → recovery, with the contact frame held.
      if (t.vis.weapon === 'none') {
        weapon.visible = false;
      } else {
        weapon.visible = true;
        weapon.texture = art.weapon;
        weapon.anchor.set(art.weaponPivotX / 48, art.weaponPivotY / 48);
        const swingAngle = swingPose(u.swing[i], t.windup);
        // The weapon hand sits on the unit's right, forward of the shoulder.
        const handDist = z * 0.2 * t.vis.scale;
        const handAngle = facing + 0.7;
        weapon.x = sx + Math.cos(handAngle) * handDist;
        weapon.y = sy + Math.sin(handAngle) * handDist;
        weapon.rotation = facing + swingAngle;
        weapon.scale.set(base * 0.5);
        weapon.tint = body.tint;
      }
    }

    this.chatter.end();

    for (let i = n; i < this.unitBodies.length; i++) {
      this.unitBodies[i].visible = false;
      this.unitWeapons[i].visible = false;
      this.unitShadows[i].visible = false;
      this.unitDiscs[i].visible = false;
    }
  }

  private unitDisc(i: number): Sprite {
    let s = this.unitDiscs[i];
    if (!s) {
      s = new Sprite(this.bank.blob);
      s.anchor.set(0.5);
      this.discLayer.addChild(s);
      this.unitDiscs[i] = s;
    }
    return s;
  }

  private unitBody(i: number): Sprite {
    let s = this.unitBodies[i];
    if (!s) {
      s = new Sprite();
      s.anchor.set(0.5);
      this.unitLayer.addChild(s);
      this.unitBodies[i] = s;
    }
    return s;
  }

  private unitWeapon(i: number): Sprite {
    let s = this.unitWeapons[i];
    if (!s) {
      s = new Sprite();
      s.anchor.set(0.2, 0.5);
      this.unitLayer.addChild(s);
      this.unitWeapons[i] = s;
    }
    return s;
  }

  private unitShadow(i: number): Sprite {
    let s = this.unitShadows[i];
    if (!s) {
      s = new Sprite(this.bank.blob);
      s.anchor.set(0.5);
      this.shadowLayer.addChild(s);
      this.unitShadows[i] = s;
    }
    return s;
  }

  // ── Projectiles ──────────────────────────────────────────────────────────

  private drawProjectiles(w: World, alpha: number): void {
    const cam = this.camera;
    const z = cam.effectiveZoom;
    const p = w.proj;
    let n = 0;
    const textures = [
      this.bank.arrow,
      this.bank.bolt,
      this.bank.stone,
      this.bank.bolt,
      this.bank.magic,
      this.bank.siegeShot,
      this.bank.magic,
    ];

    for (let i = 0; i < p.slots.high; i++) {
      if (p.slots.alive[i] === 0) continue;
      const x = p.x[i] + p.vx[i] * (alpha / 30);
      const y = p.y[i] + p.vy[i] * (alpha / 30);
      const s = this.projSprite(n++);
      s.visible = true;
      s.texture = textures[p.kind[i]] ?? this.bank.arrow;
      s.x = cam.worldToScreenX(x);
      s.y = cam.worldToScreenY(y) - p.z[i] * z * 0.5;
      s.rotation = Math.atan2(p.vy[i], p.vx[i]);
      s.scale.set((z / 40) * 1.1);
      s.tint = p.team[i] === Team.Player ? 0xffffff : 0xcdd8ee;
      s.alpha = 1;
    }
    for (let i = n; i < this.projSprites.length; i++) this.projSprites[i].visible = false;
  }

  private projSprite(i: number): Sprite {
    let s = this.projSprites[i];
    if (!s) {
      s = new Sprite();
      s.anchor.set(0.5);
      this.unitLayer.addChild(s);
      this.projSprites[i] = s;
    }
    return s;
  }

  // ── Bars, rings, banners ─────────────────────────────────────────────────

  /**
   * The day, told by light.
   *
   * A day is forty simulated seconds (`DAY_SECONDS`), and the Long Ledger has
   * been counting them since the calendar landed — this is the first time the
   * town shows one. Render-only, driven off `calendarT`, which is hashed state:
   * two replays darken on the same frame, and the simulation neither knows nor
   * cares. The curve holds full day for half the cycle so building is done in
   * honest light, dips through an amber dusk, and sits in blue-black night for
   * a quarter of it — under the fire layers, so a night siege reads as black
   * shapes and torchlight rather than as a screen going grey.
   */
  private drawNight(w: World): void {
    const g = this.nightG;
    g.clear();
    const t = (w.calendarT % DAY_SECONDS) / DAY_SECONDS;
    // Dawn at the top of the day, dusk at 0.58 — the day-half is the long half.
    let night: number;
    if (t < 0.08) night = 1 - t / 0.08;
    else if (t < 0.58) night = 0;
    else if (t < 0.74) night = (t - 0.58) / 0.16;
    else night = 1;
    // The warm band at each edge of the dark: strongest mid-transition.
    const bell = (c: number, wd: number): number => Math.max(0, 1 - Math.abs(t - c) / wd);
    const amber = Math.max(bell(0.05, 0.08), bell(0.64, 0.1)) * (1 - night * 0.6);
    if (night < 0.01 && amber < 0.02) return;
    const cam = this.camera;
    if (night >= 0.01) {
      g.rect(0, 0, cam.viewW, cam.viewH).fill({ color: 0x0c1424, alpha: 0.32 * night });
    }
    if (amber >= 0.02) {
      g.rect(0, 0, cam.viewW, cam.viewH).fill({ color: 0xc86e2e, alpha: 0.07 * amber });
    }
  }

  private drawOverlay(w: World, sel: SelectionState): void {
    const cam = this.camera;
    const z = cam.effectiveZoom;
    const g = this.overlayG;
    const gs = this.selectionG;
    g.clear();
    gs.clear();

    const b = cam.viewBounds(2);
    const u = w.units;

    // Selection rings, drawn under everything else in the overlay.
    if (sel.companies.length > 0) {
      for (const cid of sel.companies) {
        const c = w.companies.find((cc) => cc.id === cid);
        if (!c || c.dead) continue;
        for (const h of c.members) {
          const i = u.resolve(h);
          if (i < 0) continue;
          const sx = cam.worldToScreenX(u.x[i]);
          const sy = cam.worldToScreenY(u.y[i]);
          gs.ellipse(sx, sy + z * 0.12, z * 0.42, z * 0.24).stroke({
            width: 2,
            color: C.gold,
            alpha: 0.85,
          });
        }
        // Order line, so you can see where you sent them.
        if (c.orderKind !== 0) {
          gs.moveTo(cam.worldToScreenX(c.ax), cam.worldToScreenY(c.ay))
            .lineTo(cam.worldToScreenX(c.orderX), cam.worldToScreenY(c.orderY))
            .stroke({ width: 1.5, color: C.gold, alpha: 0.4 });
          gs.circle(cam.worldToScreenX(c.orderX), cam.worldToScreenY(c.orderY), z * 0.25).stroke({
            width: 2,
            color: C.gold,
            alpha: 0.6,
          });
        }
      }
    }

    // The open building, ringed on its own footprint so the panel on the right
    // is unambiguously *about* something you can see.
    const selB = sel.building ?? -1;
    if (selB >= 0 && w.buildings.slots.alive[selB] === 1) {
      const def = buildingType(w.buildings.typeId[selB]);
      const x0 = cam.worldToScreenX(w.buildings.tx[selB]);
      const y0 = cam.worldToScreenY(w.buildings.ty[selB]);
      const bw = def.w * z;
      const bh = def.h * z;
      gs.rect(x0, y0, bw, bh).fill({ color: C.gold, alpha: 0.09 });
      // Corner brackets rather than a full box: a closed rectangle reads as a
      // build preview, which is the one thing it must not be mistaken for.
      const c = Math.min(bw, bh) * 0.32 + 3;
      for (const [cx, cy, sx, sy] of [
        [x0, y0, 1, 1],
        [x0 + bw, y0, -1, 1],
        [x0, y0 + bh, 1, -1],
        [x0 + bw, y0 + bh, -1, -1],
      ]) {
        gs.moveTo(cx + sx * c, cy)
          .lineTo(cx, cy)
          .lineTo(cx, cy + sy * c)
          .stroke({ width: 2.2, color: C.goldPale, alpha: 0.95 });
      }
    }

    // Health bars: damaged units only, plus everything big, always.
    //
    // These were the loudest thing on screen in the first pass — a wall of
    // green over the brawl. They are now narrow, tight to the head, and only
    // appear once a unit has actually lost something worth noticing.
    const barW = Math.max(7, z * 0.34);
    for (let i = 0; i < u.slots.high; i++) {
      if (u.slots.alive[i] === 0) continue;
      const x = u.x[i];
      const y = u.y[i];
      if (x < b.x0 || x > b.x1 || y < b.y0 || y > b.y1) continue;
      const frac = u.hp[i] / Math.max(1, u.maxHp[i]);
      const important = (u.flags[i] & (UF.Champion | UF.Siege | UF.Large)) !== 0;
      if (frac > 0.92 && !important) continue;
      if (z < ZOOM_TOKEN_THRESHOLD && !important) continue;

      const sx = cam.worldToScreenX(x);
      const sy = cam.worldToScreenY(y) - z * (important ? 0.62 : 0.44);
      /**
       * Readable at a glance, still quiet in a brawl.
       *
       * The first pass was a wall of green and the second overcorrected into
       * a hairline nobody could read at speed — two pixels of colour on a
       * dark ground, at the moment a player most needs to know which of two
       * lines is losing. A little taller, a dark surround that separates it
       * from whatever is behind, and the missing part drawn rather than left
       * empty, so the bar reads as a proportion instead of a smear.
       */
      const h = important ? 5 : 3.5;
      const wBar = important ? barW * 2.2 : barW;
      g.rect(sx - wBar / 2 - 1, sy - 1, wBar + 2, h + 2).fill({ color: 0x0d0b09, alpha: 0.92 });
      g.rect(sx - wBar / 2, sy, wBar, h).fill({ color: 0x3a2320, alpha: 0.85 });
      const col = frac > 0.6 ? C.hpGood : frac > 0.3 ? C.hpMid : C.hpBad;
      g.rect(sx - wBar / 2, sy, wBar * clamp01(frac), h).fill(
        u.team[i] === Team.Player ? col : shade(col, 0.82),
      );
      if (u.shieldT[i] > 0) {
        g.rect(sx - wBar / 2, sy - 2.5, wBar, 1.5).fill({ color: C.will, alpha: 0.9 });
      }
    }

    // Company banners: strength as a fraction, floating above the formation.
    for (const c of w.companies) {
      if (c.dead || c.members.length === 0) continue;
      if (c.ax < b.x0 || c.ax > b.x1 || c.ay < b.y0 || c.ay > b.y1) continue;
      const sx = cam.worldToScreenX(c.ax);
      const sy = cam.worldToScreenY(c.ay) - z * 1.35;
      const t = unitType(c.typeIdx);
      const fac = faction(t.faction === 'common' ? w.faction.id : t.faction);
      const accent = c.team === Team.Player ? fac.accent : mix(fac.accent, C.hostileRim, 0.5);
      const strength = c.members.length / Math.max(1, c.maxSize);

      const bw = Math.max(22, z * 0.9);
      const bh = Math.max(9, z * 0.32);
      g.poly([
        sx - bw / 2,
        sy,
        sx + bw / 2,
        sy,
        sx + bw / 2 - bh * 0.35,
        sy + bh,
        sx - bw / 2 + bh * 0.35,
        sy + bh,
      ]).fill({ color: 0x14120f, alpha: 0.8 });
      g.rect(sx - bw / 2 + 2, sy + bh - 3, (bw - 4) * strength, 2.4).fill(accent);
      g.poly([
        sx - bw / 2,
        sy,
        sx + bw / 2,
        sy,
        sx + bw / 2 - bh * 0.35,
        sy + bh,
        sx - bw / 2 + bh * 0.35,
        sy + bh,
      ]).stroke({ width: 1.2, color: accent, alpha: 0.8 });

      if (sel.companies.includes(c.id)) {
        g.circle(sx, sy + bh / 2, bw * 0.62).stroke({ width: 1.5, color: C.goldPale, alpha: 0.5 });
      }
    }

    // Battle mode: the deployment ground, marked out like a muster field.
    if (w.battle?.deploying) {
      const dz = w.battle.deployZone;
      const x0 = cam.worldToScreenX(dz.x0);
      const y0 = cam.worldToScreenY(dz.y0);
      const x1 = cam.worldToScreenX(dz.x1);
      const y1 = cam.worldToScreenY(dz.y1);
      g.rect(x0, y0, x1 - x0, y1 - y0).fill({ color: C.gold, alpha: 0.06 });
      g.rect(x0, y0, x1 - x0, y1 - y0).stroke({ width: 2, color: C.gold, alpha: 0.5 });
      // Hatch the edge so it reads as a boundary rather than a building plot.
      for (let sx = x0; sx < x1; sx += 16) {
        g.moveTo(sx, y0)
          .lineTo(sx + 8, y0 - 8)
          .stroke({ width: 1.5, color: C.gold, alpha: 0.32 });
      }
    }

    // Invasion gates, so you always know where they can come from.
    if (w.phase === Phase.Build && !w.battle) {
      for (let gi = 0; gi < w.map.gates.length; gi++) {
        const gate = w.map.gates[gi];
        const incoming = w.incomingGates.includes(gi);
        const sx = cam.worldToScreenX(gate.x + 0.5);
        const sy = cam.worldToScreenY(gate.y + 0.5);
        const r = z * 1.1;
        g.circle(sx, sy, r).stroke({
          width: incoming ? 3 : 1.5,
          color: incoming ? 0xd1352c : C.boneDeep,
          alpha: incoming ? 0.9 : 0.35,
        });
        if (incoming) {
          const dx = w.map.hearthX - gate.x;
          const dy = w.map.hearthY - gate.y;
          const l = Math.hypot(dx, dy) || 1;
          g.moveTo(sx, sy)
            .lineTo(sx + (dx / l) * r * 2.4, sy + (dy / l) * r * 2.4)
            .stroke({ width: 3, color: 0xd1352c, alpha: 0.6 });
        }
      }
    }
  }

  // ── Build preview ────────────────────────────────────────────────────────

  private drawPreview(w: World, preview?: BuildPreview): void {
    const g = this.previewG;
    g.clear();
    if (!preview) return;
    const cam = this.camera;
    const z = cam.effectiveZoom;

    if (preview.kind === 'building') {
      const def = buildingType(preview.typeIdx);
      const ok = preview.valid;
      const sx = cam.worldToScreenX(preview.x);
      const sy = cam.worldToScreenY(preview.y);
      g.rect(sx, sy, def.w * z, def.h * z).fill({
        color: ok ? 0x6fa84a : 0xa8302f,
        alpha: 0.22,
      });
      g.rect(sx, sy, def.w * z, def.h * z).stroke({
        width: 2,
        color: ok ? 0x9fd06a : 0xd1352c,
        alpha: 0.9,
      });
    } else if (preview.kind === 'tower') {
      const ok = preview.valid;
      const sx = cam.worldToScreenX(preview.x);
      const sy = cam.worldToScreenY(preview.y);
      const wpx = TOWER_SIZE * z;
      const rise = z * 0.8;
      const tint = ok ? 0x9fd06a : 0xd1352c;
      g.rect(sx, sy - rise, wpx, wpx + rise).fill({ color: ok ? 0x6fa84a : 0xa8302f, alpha: 0.2 });
      g.rect(sx, sy - rise, wpx, wpx + rise).stroke({ width: 2, color: tint, alpha: 0.9 });
      g.rect(sx, sy, wpx, wpx).stroke({ width: 1.5, color: tint, alpha: 0.5 });
    } else if (preview.kind === 'line') {
      for (const [tx, ty] of preview.tiles) {
        const sx = cam.worldToScreenX(tx);
        const sy = cam.worldToScreenY(ty);
        const ok = w.map.canBuild(tx, ty);
        g.rect(sx + 1, sy + 1, z - 2, z - 2).fill({
          color: ok ? 0x6fa84a : 0xa8302f,
          alpha: 0.28,
        });
      }
    } else if (preview.kind === 'power') {
      const sx = cam.worldToScreenX(preview.x);
      const sy = cam.worldToScreenY(preview.y);
      const r = preview.radius * z;
      g.circle(sx, sy, r).fill({ color: C.gold, alpha: 0.1 });
      g.circle(sx, sy, r).stroke({ width: 2.5, color: C.goldPale, alpha: 0.9 });
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2 + this.time * 0.35;
        g.moveTo(sx + Math.cos(a) * r * 0.9, sy + Math.sin(a) * r * 0.9)
          .lineTo(sx + Math.cos(a) * r * 1.08, sy + Math.sin(a) * r * 1.08)
          .stroke({ width: 2, color: C.goldPale, alpha: 0.7 });
      }
      if (preview.friendlyFire) {
        g.circle(sx, sy, r * 0.7).stroke({ width: 1.5, color: 0xd1352c, alpha: 0.5 });
      }
    } else if (preview.kind === 'drag') {
      // Total War-style formation drag: width and facing from the drag vector.
      const ax = cam.worldToScreenX(preview.x);
      const ay = cam.worldToScreenY(preview.y);
      const bx = cam.worldToScreenX(preview.x2);
      const by = cam.worldToScreenY(preview.y2);
      const dx = bx - ax;
      const dy = by - ay;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 2, color: C.gold, alpha: 0.85 });
      const depth = z * 0.55;
      g.poly([ax, ay, bx, by, bx + nx * depth, by + ny * depth, ax + nx * depth, ay + ny * depth]).fill({
        color: C.gold,
        alpha: 0.18,
      });
      // Facing arrows along the front.
      const steps = Math.max(2, Math.min(14, Math.round(len / 26)));
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const px = ax + dx * t;
        const py = ay + dy * t;
        g.moveTo(px, py)
          .lineTo(px - nx * z * 0.32, py - ny * z * 0.32)
          .stroke({ width: 2, color: C.goldPale, alpha: 0.8 });
      }
    }
  }

  clear(): void {
    this.fx.clear();
    this.log.length = 0;
    this.bossCard = null;
    this.curtainG.clear();
    this.markG.clear();
    for (const s of this.chunkSprites) if (s) s.visible = false;
    this.chunkVersion.fill(-1);
  }
}

export type BuildPreview =
  | { kind: 'building'; typeIdx: number; x: number; y: number; valid: boolean }
  /** A tower's 2×2 block, ghosted at the height it will stand at. */
  | { kind: 'tower'; x: number; y: number; valid: boolean }
  | { kind: 'line'; tiles: Array<[number, number]> }
  | { kind: 'power'; x: number; y: number; radius: number; friendlyFire: boolean }
  | { kind: 'drag'; x: number; y: number; x2: number; y2: number };

/**
 * The attack animation curve. Wind up slowly, snap through the contact frame,
 * then recover. The snap is what the eye reads as force.
 */
function swingPose(swing: number, windup: number): number {
  if (swing <= 0) return 0;
  if (swing < windup) {
    const t = swing / Math.max(0.001, windup);
    return -0.95 * (t * t);
  }
  if (swing < windup + CONTACT_HOLD) {
    const t = (swing - windup) / CONTACT_HOLD;
    return lerp(-0.95, 0.8, Math.min(1, t * 2.4));
  }
  const t = (swing - windup - CONTACT_HOLD) / Math.max(0.001, 1 - windup - CONTACT_HOLD);
  return lerp(0.8, 0, clamp01(t));
}
